import { HomeHeader } from "@/components/home/HomeHeader";
import { usePageHeader } from "@/hooks/usePageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RunSummaryWizard } from "@/components/run/RunSummaryWizard";
import { RunMatchNotification } from "@/components/runmatch/RunMatchNotification";
import { DuoRunMode } from "@/components/runmatch/DuoRunMode";
import { DuoRunSummary } from "@/components/runmatch/DuoRunSummary";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { useTracking } from "@/hooks/useTracking";
import { TrackingPoint, TrackingMetrics as TrackingServiceMetrics } from "@/services/tracking-service";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { offlineDB, OfflineRun } from "@/lib/offline-db";
import { useBPM } from "@/hooks/useBPM";
import { useAdaptiveMusic } from "@/hooks/useAdaptiveMusic";
import { useRunMusicHistory } from "@/hooks/useRunMusicHistory";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RunMap } from "@/components/map/RunMap";
import { EnhancedRunMap } from "@/components/map/EnhancedRunMap";
import { ElevationProfile } from "@/components/run/ElevationProfile";
import { RealTimeAICoach } from "@/components/run/RealTimeAICoach";
import { AdaptiveMusicPlayer } from "@/components/music/AdaptiveMusicPlayer";
import { CompactMusicPlayer } from "@/components/music/CompactMusicPlayer";
import { BadgeUnlockAnimation } from "@/components/achievements/BadgeUnlockAnimation";
import { analytics } from "@/lib/analytics";
import { Play, Pause, Square, MapPin, EyeOff, Eye, Music, Heart, Clock, Route, Zap, Flame, BarChart3, Map, Loader2, CheckCircle, AlertCircle, X, Signal } from "lucide-react";
import { GPSDiagnosticBadge } from "@/components/run/GPSDiagnosticBadge";
import { useState, useEffect, useRef } from "react";
import { TrackingMetrics } from "@/components/tracking/TrackingMetrics";
import { logger } from "@/lib/logger";
import { BPMDisplay } from "@/components/tracking/BPMDisplay";
import { HeartRateCard } from "@/components/run/HeartRateCard";
import { RealTimeFeedback } from "@/components/tracking/RealTimeFeedback";
import { useNativeHaptics } from "@/hooks/useNativeHaptics";
import confetti from "canvas-confetti";
import { PostureAnalysis } from "@/components/posture/PostureAnalysis";
import { postureAnalysisService } from "@/services/posture-analysis-service";
import { ActiveSegmentOverlay } from "@/components/segments/ActiveSegmentOverlay";
import { RouteSuggestions } from "@/components/routes/RouteSuggestions";
import { TouristRoutesSheet } from "@/components/routes/TouristRoutesSheet";
import { useSegmentDetection } from "@/hooks/useSegmentDetection";
import { useRouteSuggestions } from "@/hooks/useRouteSuggestions";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { LocationPermissionDialog } from "@/components/run/LocationPermissionDialog";
import { BLEConnectionDialog } from "@/components/run/BLEConnectionDialog";
import { BPMStatusDialog } from "@/components/run/BPMStatusDialog";
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useRunSession } from '@/store/run-session';
import { useNavigate } from 'react-router-dom';
import { trackingService } from '@/services/tracking-service';
import { useUserAge } from '@/hooks/useUserAge';

// Helper function: Calculate distance between two GPS points using Haversine formula
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Default fallback location (São Paulo) - NEVER use (0,0)
const DEFAULT_FALLBACK_LOCATION = { lat: -23.5505, lng: -46.6333 };

// Helper to get safe map center - NEVER returns (0,0)
const getSafeMapCenter = (
  polyline: { lat: number; lng: number }[],
  trackingPoints: { lat: number; lng: number }[],
  initialLocation: { lat: number; lng: number } | null
): { lat: number; lng: number } => {
  // Priority 1: Last tracking point
  if (trackingPoints.length > 0) {
    const last = trackingPoints[trackingPoints.length - 1];
    if (last.lat !== 0 || last.lng !== 0) return last;
  }
  // Priority 2: Last polyline point
  if (polyline.length > 0) {
    const last = polyline[polyline.length - 1];
    if (last.lat !== 0 || last.lng !== 0) return last;
  }
  // Priority 3: Service backup
  const servicePos = trackingService.getLastKnownPosition();
  if (servicePos && (servicePos.lat !== 0 || servicePos.lng !== 0)) return servicePos;
  // Priority 4: Initial location
  if (initialLocation && (initialLocation.lat !== 0 || initialLocation.lng !== 0)) return initialLocation;
  // Fallback: São Paulo (never 0,0)
  return DEFAULT_FALLBACK_LOCATION;
};
const Run = () => {
  // Registrar header no contexto - será renderizado FORA do main pelo AppShell
  usePageHeader(<HomeHeader />);
  
  const navigate = useNavigate();
  const location = window.location;
  
  
  const runSession = useRunSession();
  const {
    success: hapticSuccess
  } = useNativeHaptics();
  const networkStatus = useNetworkStatus();

  // Strava-class tracking with Kalman filter
  const tracking = useTracking();

  // Local state for run metrics
  const [state, setState] = useState({
    status: 'idle',
    distance: 0,
    duration: 0,
    pace: 0,
    avgPace: 0,
    calories: 0,
    splits: [] as Array<{
      km: number;
      time: number;
      pace: number;
      latitude?: number;
      longitude?: number;
    }>,
    polyline: [] as Array<{
      lat: number;
      lng: number;
    }>,
    currentSpeed: 0,
  });

  // Sync local state with global store
  useEffect(() => {
    if (runSession.status === 'paused' && state.status !== 'paused') {
      logger.info('Syncing local state to paused from global store');
      setState(prev => ({ ...prev, status: 'paused' }));
    }
    if (runSession.status === 'running' && state.status !== 'running') {
      logger.info('Syncing local state to running from global store');
      setState(prev => ({ ...prev, status: 'running' }));
    }
  }, [runSession.status, state.status]);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const userAge = useUserAge();
  const bpm = useBPM(userAge);

  // Adaptive Music with BPM sync
  const [autoSync, setAutoSync] = useState(true);
  const adaptiveMusic = useAdaptiveMusic({
    isRunning: state.status === 'running',
    autoSync
  });

  // Music History Tracking
  const musicHistory = useRunMusicHistory();

  // Segment Detection & Route Suggestions
  const {
    user
  } = useAuth();
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const currentLat = state.polyline.length > 0 ? state.polyline[state.polyline.length - 1].lat : null;
  const currentLng = state.polyline.length > 0 ? state.polyline[state.polyline.length - 1].lng : null;
  const {
    nearbySegments,
    activeSegment
  } = useSegmentDetection(currentLat, currentLng, currentRunId, user?.id || null);
  const routeSuggestions = useRouteSuggestions(user?.id || null);
  const [showRouteSuggestions, setShowRouteSuggestions] = useState(false);
  const [showTouristRoutes, setShowTouristRoutes] = useState(false);
  const {
    toast
  } = useToast();

  // GPS Diagnostic State
  const [gpsStats, setGpsStats] = useState({
    pointsCollected: 0,
    pointsRejected: 0,
    currentAccuracy: undefined as number | undefined,
    isWarmingUp: true
  });
  const [hasShownGPSWarning, setHasShownGPSWarning] = useState(false);

  // Update duration timer
  useEffect(() => {
    if (state.status === 'running') {
      durationTimerRef.current = setInterval(() => {
        // Guard: Validate startTimeRef is initialized correctly
        if (startTimeRef.current === 0 || startTimeRef.current > Date.now()) {
          logger.error('Invalid startTimeRef detected, resetting', { 
            startTimeRef: startTimeRef.current,
            duration: state.duration 
          });
          startTimeRef.current = Date.now() - (state.duration * 1000);
        }

        const elapsed = Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000);
        
        // Sanity check: elapsed should be positive and less than 24h (86400s)
        const safeElapsed = (elapsed > 0 && elapsed < 86400) ? elapsed : state.duration;
        
        if (elapsed !== safeElapsed) {
          logger.error('Timer corruption detected', { 
            calculated: elapsed, 
            capped: safeElapsed,
            startTimeRef: startTimeRef.current 
          });
        }
        
        setState(prev => ({
          ...prev,
          duration: safeElapsed
        }));
      }, 1000);
    } else {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    }
    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, [state.status]);

  // Update GPS diagnostic stats
  useEffect(() => {
    if (state.status === 'running' && tracking.points) {
      const rejected = (tracking as any).getRejectedPointsStats?.() || {
        total: 0,
        accuracy: 0,
        speed: 0,
        distance: 0
      };
      const lastPoint = tracking.points[tracking.points.length - 1];
      const total = tracking.points.length + (rejected.total || 0);
      const rejectionRate = total > 0 ? (rejected.total || 0) / total * 100 : 0;
      setGpsStats({
        pointsCollected: tracking.points.length,
        pointsRejected: rejected.total || 0,
        currentAccuracy: lastPoint?.accuracy,
        isWarmingUp: tracking.points.length < 20
      });

      // Warning toast if high rejection rate (only show once)
      if (!hasShownGPSWarning && total > 20 && rejectionRate > 50) {
        setHasShownGPSWarning(true);
        toast({
          title: "⚠️ Sinal GPS Fraco",
          description: `${Math.round(rejectionRate)}% dos pontos GPS estão sendo rejeitados. Tente mover-se para área aberta sem prédios ou árvores.`,
          variant: "destructive",
          duration: 8000
        });
      }
    }
  }, [tracking.points, state.status, hasShownGPSWarning, toast]);

  // Calculate metrics from tracking points
  useEffect(() => {
    // Allow single point to show position immediately
    if (tracking.points.length === 0) return;

    // If only 1 point, just set polyline to show position
    if (tracking.points.length === 1) {
      const firstPoint = tracking.points[0];
      setState(prev => ({
        ...prev,
        polyline: [{ lat: firstPoint.lat, lng: firstPoint.lng }]
      }));
      return;
    }

    // Calculate total distance from filtered points
    let totalDistance = 0;
    const polyline: Array<{
      lat: number;
      lng: number;
    }> = [];
    for (let i = 0; i < tracking.points.length; i++) {
      const point = tracking.points[i];
      polyline.push({
        lat: point.lat,
        lng: point.lng
      });
      if (i > 0) {
        const prevPoint = tracking.points[i - 1];
        const segmentDistance = haversineDistance(prevPoint.lat, prevPoint.lng, point.lat, point.lng);
        totalDistance += segmentDistance;
      }
    }

    // Calculate pace (min/km) - Usar validação científica
    const distanceKm = totalDistance / 1000;
    const durationMin = state.duration / 60;
    let pace = 0;
    
    // Distância mínima aumentada para 50m para cálculos mais confiáveis
    // (GPS pode ter erro de 10-50m, então 5m é muito pouco)
    const MIN_DISTANCE_FOR_PACE_KM = 0.05; // 50 metros
    
    if (distanceKm >= MIN_DISTANCE_FOR_PACE_KM && durationMin > 0) {
      const rawPace = durationMin / distanceKm;
      
      // Usar validação científica baseada em fisiologia humana
      // MIN_PACE = 2.5 min/km (recorde mundial de sprint)
      // MAX_PACE = 20 min/km (caminhada muito lenta)
      if (rawPace >= 2.5 && rawPace <= 20) {
        pace = rawPace;
      }
    }

    // Estimate calories (rough: 60 kcal/km for average runner)
    const calories = Math.round(distanceKm * 60);

    // Calculate splits (1km each)
    const splits: Array<{
      km: number;
      time: number;
      pace: number;
      latitude?: number;
      longitude?: number;
    }> = [];
    let splitDistance = 0;
    let splitStartTime = tracking.points[0]?.timestamp || Date.now();
    let splitIndex = 1;
    for (let i = 1; i < tracking.points.length; i++) {
      const prevPoint = tracking.points[i - 1];
      const point = tracking.points[i];
      const segmentDistance = haversineDistance(prevPoint.lat, prevPoint.lng, point.lat, point.lng);
      splitDistance += segmentDistance;
      if (splitDistance >= 1000) {
        const splitTime = (point.timestamp - splitStartTime) / 1000;
        const splitPace = splitTime / 60;
        splits.push({
          km: splitIndex,
          time: splitTime,
          pace: splitPace,
          latitude: point.lat,
          longitude: point.lng
        });
        splitDistance = 0;
        splitStartTime = point.timestamp;
        splitIndex++;
      }
    }

    // Calculate current speed (m/s) - Usar janela móvel de 5s para suavizar
    let currentSpeed = 0;
    if (tracking.points.length >= 2) {
      const WINDOW_SECONDS = 5;
      const now = tracking.points[tracking.points.length - 1]?.timestamp || Date.now();
      const windowStart = now - (WINDOW_SECONDS * 1000);
      
      // Filtrar pontos dentro da janela de tempo
      const windowPoints = tracking.points.filter(p => p.timestamp >= windowStart);
      
      if (windowPoints.length >= 2) {
        // Calcular distância total dentro da janela
        let windowDistance = 0;
        for (let i = 1; i < windowPoints.length; i++) {
          const prev = windowPoints[i - 1];
          const curr = windowPoints[i];
          windowDistance += haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng);
        }
        
        const windowTime = (windowPoints[windowPoints.length - 1].timestamp - windowPoints[0].timestamp) / 1000;
        
        // Calcular velocidade média da janela (mais estável que entre 2 pontos)
        if (windowTime > 0.5) { // Mínimo 0.5s para evitar divisão por valores muito pequenos
          const rawSpeed = windowDistance / windowTime;
          // Limitar a velocidade máxima humanamente possível (15 m/s com margem GPS)
          currentSpeed = Math.min(rawSpeed, 15);
        }
      } else {
        // Fallback: usar apenas 2 últimos pontos se janela tiver poucos pontos
        const lastPoint = tracking.points[tracking.points.length - 1];
        const prevPoint = tracking.points[tracking.points.length - 2];
        const distance = haversineDistance(prevPoint.lat, prevPoint.lng, lastPoint.lat, lastPoint.lng);
        const timeDiff = (lastPoint.timestamp - prevPoint.timestamp) / 1000;
        if (timeDiff > 0) {
          currentSpeed = Math.min(distance / timeDiff, 15);
        }
      }
    }
    setState(prev => ({
      ...prev,
      distance: totalDistance,
      pace,
      calories,
      splits,
      polyline,
      currentSpeed
    }));
  }, [tracking.points, state.duration]);

  // GPS validation and auto-pause detection
  useEffect(() => {
    if (state.status !== 'running') return;

    // Consider GPS active with just 1 point (immediate feedback) - no toast, just badge
    if (tracking.points.length >= 1 && gpsStats.isWarmingUp) {
      setGpsStats(prev => ({ ...prev, isWarmingUp: false }));
      logger.info('GPS is active - first point received', {
        pointsCollected: tracking.points.length
      });
      // ✅ Silent: GPSDiagnosticBadge shows status visually, no toast spam
    }

    // ⚡ Verificar GPS após 10s (increased from 5s) - silent check, no toast
    const gpsCheckTimeout = setTimeout(() => {
      if (tracking.points.length === 0) {
        logger.warn('GPS warming up - no points after 10s', {
          status: state.status
        });
        // ⚡ No toast - just log, GPS badge shows status visually
      }
    }, 10000);
    
    // Verificar após 15s - erro real
    const gpsErrorTimeout = setTimeout(() => {
      if (tracking.points.length === 0) {
        logger.error('GPS not collecting points after 15s', {
          status: state.status
        });
        toast({
          title: 'GPS não está respondendo',
          description: 'Verifique as permissões e se o GPS está ativo.',
          variant: 'destructive'
        });
      }
    }, 15000);
    
    return () => {
      clearTimeout(gpsCheckTimeout);
      clearTimeout(gpsErrorTimeout);
    };
  }, [state.status, tracking.points.length, gpsStats.isWarmingUp]);

  // 🔄 Persist run state when app goes to background (Native)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listenerHandle: any;
    CapacitorApp.addListener('pause', () => {
      if (state.status === 'running' || state.status === 'paused') {
        logger.info('App going to background, saving run state', {
          status: state.status,
          duration: state.duration,
          distance: state.distance
        });
        const runState = {
          status: state.status,
          duration: state.duration,
          distance: state.distance,
          pace: state.pace,
          calories: state.calories,
          splits: state.splits,
          polyline: state.polyline,
          currentSpeed: state.currentSpeed,
          startTime: startTimeRef.current,
          pausedDuration: pausedDurationRef.current,
          timestamp: Date.now()
        };
        sessionStorage.setItem('active_run_state', JSON.stringify(runState));
        logger.info('✅ Run state saved to sessionStorage');
      }
    }).then(handle => {
      listenerHandle = handle;
    });
    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [state]);

  // 🔄 Restore run state when app returns from background (Native)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listenerHandle: any;
    CapacitorApp.addListener('resume', () => {
      // Don't restore if run was finished
      const isFinished = sessionStorage.getItem('run_finished') === 'true';
      if (isFinished) {
        logger.info('App resumed but run was finished, not restoring');
        return;
      }

      const savedState = sessionStorage.getItem('active_run_state');
      if (!savedState) return;
      try {
        const runState = JSON.parse(savedState);
        
        // Only restore if status is running or paused
        if (runState.status !== 'running' && runState.status !== 'paused') {
          logger.info('Saved state is not active, not restoring', { status: runState.status });
          sessionStorage.removeItem('active_run_state');
          return;
        }

        const timeSinceSave = Date.now() - runState.timestamp;
        if (timeSinceSave < 3600000) {
          logger.info('App resumed, restoring run state', {
            status: runState.status,
            duration: runState.duration,
            timeSinceSave: Math.round(timeSinceSave / 1000) + 's'
          });
          setState({
            status: runState.status,
            duration: runState.duration,
            distance: runState.distance,
            pace: runState.pace,
            avgPace: runState.avgPace ?? runState.pace ?? 0,
            calories: runState.calories,
            splits: runState.splits,
            polyline: runState.polyline,
            currentSpeed: runState.currentSpeed
          });
          startTimeRef.current = runState.startTime;
          pausedDurationRef.current = runState.pausedDuration;
          toast({
            title: "Corrida Recuperada",
            description: "Bem-vindo de volta! Sua corrida continua de onde parou.",
            duration: 3000
          });
        } else {
          sessionStorage.removeItem('active_run_state');
          logger.warn('Run state too old, cleared', {
            timeSinceSave
          });
        }
      } catch (error) {
        logger.error('Failed to restore run state', error);
        sessionStorage.removeItem('active_run_state');
      }
    }).then(handle => {
      listenerHandle = handle;
    });
    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, []);

  // 🔄 Check for saved run state on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('active_run_state');
    if (!savedState) return;
    try {
      const runState = JSON.parse(savedState);
      const timeSinceSave = Date.now() - runState.timestamp;
      if (timeSinceSave < 600000 && (runState.status === 'running' || runState.status === 'paused')) {
        logger.info('Found recent run state on mount, offering to restore', {
          status: runState.status,
          duration: runState.duration,
          timeSinceSave: Math.round(timeSinceSave / 1000) + 's'
        });
        toast({
          title: "Corrida Anterior Detectada",
          description: `Você tem uma corrida ${runState.status === 'paused' ? 'pausada' : 'em andamento'} de ${Math.floor(runState.duration / 60)}min. Deseja continuar?`,
          action: <Button size="sm" onClick={() => {
            setState({
              status: runState.status,
              duration: runState.duration,
              distance: runState.distance,
              pace: runState.pace,
              avgPace: runState.avgPace ?? runState.pace ?? 0,
              calories: runState.calories,
              splits: runState.splits,
              polyline: runState.polyline,
              currentSpeed: runState.currentSpeed
            });
            startTimeRef.current = runState.startTime;
            pausedDurationRef.current = runState.pausedDuration;
            if (runState.status === 'running') {
              tracking.resumeTracking();
            }
            toast({
              title: "✅ Corrida Recuperada",
              description: "Continue de onde parou!"
            });
          }}>
              Continuar
            </Button>,
          duration: 10000
        });
      } else if (timeSinceSave >= 600000) {
        sessionStorage.removeItem('active_run_state');
        logger.info('Old run state cleared', {
          timeSinceSave
        });
      }
    } catch (error) {
      logger.error('Failed to check for saved run state', error);
      sessionStorage.removeItem('active_run_state');
    }
  }, []);

  // 🔄 Fallback for web: Page Visibility API
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (state.status === 'running' || state.status === 'paused') {
          logger.info('Page hidden, saving run state');
          const runState = {
            status: state.status,
            duration: state.duration,
            distance: state.distance,
            pace: state.pace,
            calories: state.calories,
            splits: state.splits,
            polyline: state.polyline,
            currentSpeed: state.currentSpeed,
            startTime: startTimeRef.current,
            pausedDuration: pausedDurationRef.current,
            timestamp: Date.now()
          };
          sessionStorage.setItem('active_run_state', JSON.stringify(runState));
        }
      } else {
        // Don't restore if run was finished
        const isFinished = sessionStorage.getItem('run_finished') === 'true';
        if (isFinished) {
          logger.info('Page visible but run was finished, not restoring');
          return;
        }

        const savedState = sessionStorage.getItem('active_run_state');
        if (savedState) {
          try {
            const runState = JSON.parse(savedState);
            
            // Only restore if status is running or paused
            if (runState.status !== 'running' && runState.status !== 'paused') {
              logger.info('Saved state is not active, not restoring', { status: runState.status });
              sessionStorage.removeItem('active_run_state');
              return;
            }

            const timeSinceSave = Date.now() - runState.timestamp;
            if (timeSinceSave < 600000) {
              logger.info('Page visible, restoring run state');
              setState({
                status: runState.status,
                duration: runState.duration,
                distance: runState.distance,
                pace: runState.pace,
                avgPace: runState.avgPace ?? runState.pace ?? 0,
                calories: runState.calories,
                splits: runState.splits,
                polyline: runState.polyline,
                currentSpeed: runState.currentSpeed
              });
              startTimeRef.current = runState.startTime;
              pausedDurationRef.current = runState.pausedDuration;
            }
          } catch (error) {
            logger.error('Failed to restore run state from visibility change', error);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state]);

  // Control functions
  const startRun = async () => {
    const span = logger.startSpan('startRun');
    try {
      logger.info('Starting run', {
        timestamp: Date.now(),
        deviceInfo: {
          platform: navigator.platform,
          userAgent: navigator.userAgent
        }
      });
      
      // Start tracking (service generates its own sessionId internally)
      await tracking.startTracking();
      
      // Get session ID from tracking service and update global store
      const sessionId = trackingService.getSessionId();
      if (sessionId) {
        runSession.startRun(sessionId);
      }
      
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      setHasShownGPSWarning(false); // Reset GPS warning flag for new run
      setState(prev => ({
        ...prev,
        status: 'running'
      }));

      // Start music history session with temporary ID
      const tempRunId = `temp-${Date.now()}`;
      musicHistory.startSession(tempRunId);

      // BPM monitoring is manual - user must click to connect
      logger.info('Run started. BPM monitoring available but not auto-started');
      span.end('success');
    } catch (error) {
      span.end('error', {
        error
      });
      throw error;
    }
  };
  const pauseRun = () => {
    logger.info('Pausing run', {
      duration: state.duration,
      distance: state.distance
    });
    
    // Update global store
    runSession.pauseRun();
    runSession.updateMetrics({
      duration: state.duration,
      distance: state.distance,
      pace: state.pace,
    });
    
    tracking.pauseTracking();
    setState(prev => ({
      ...prev,
      status: 'paused'
    }));
    
    toast({
      title: 'Corrida pausada',
      description: 'Clique em retomar quando estiver pronto.',
      duration: 2000,
    });
  };
  const resumeRun = () => {
    // Don't resume if run was marked as finished
    if (sessionStorage.getItem('run_finished') === 'true') {
      logger.warn('Attempted to resume finished run, ignoring');
      return;
    }
    
    logger.info('Resuming run');
    
    // Update global store
    runSession.resumeRun();
    
    const pausedTime = Date.now() - (startTimeRef.current + state.duration * 1000);
    pausedDurationRef.current += pausedTime;
    setState(prev => ({
      ...prev,
      status: 'running'
    }));
    
    toast({
      title: 'Corrida retomada',
      description: 'Continue correndo!',
      duration: 2000,
    });
  };
  const finishRun = () => {
    logger.info('Finishing run', {
      duration: state.duration,
      distance: state.distance,
      pace: state.pace
    });
    setState(prev => ({
      ...prev,
      status: 'finished'
    }));

    // Mark run as finished to prevent state restoration
    sessionStorage.setItem('run_finished', 'true');
    
    // Clear saved run state
    sessionStorage.removeItem('active_run_state');
    logger.info('Cleared saved run state and marked as finished');
  };
  const resetRun = () => {
    logger.info('Resetting run');
    setState({
      status: 'idle',
      duration: 0,
      distance: 0,
      pace: 0,
      avgPace: 0,
      calories: 0,
      splits: [],
      polyline: [],
      currentSpeed: 0
    });
    startTimeRef.current = 0;
    pausedDurationRef.current = 0;
    setHasShownGPSWarning(false); // Reset GPS warning flag

    // Clear saved run state and finished flag
    sessionStorage.removeItem('active_run_state');
    sessionStorage.removeItem('run_finished');
    logger.info('Cleared saved run state and finished flag');
  };
  const [showMap, setShowMap] = useState(false);
  const [processedRunId, setProcessedRunId] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [invisibleMode, setInvisibleMode] = useState(false);
  const [currentEncounter, setCurrentEncounter] = useState<any>(null);
  const [duoRunMode, setDuoRunMode] = useState(false);
  const [partnerLocation, setPartnerLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [showDuoSummary, setShowDuoSummary] = useState(false);
  const [showBadgeAnimation, setShowBadgeAnimation] = useState(false);
  const [unlockedBadges, setUnlockedBadges] = useState<any[]>([]);
  const [shirtLevelUp, setShirtLevelUp] = useState<any>(null);
  const [duoRunData, setDuoRunData] = useState<any>(null);
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [postureData, setPostureData] = useState<any>(null);
  const [initialLocation, setInitialLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [isFirstRun, setIsFirstRun] = useState(false);
  const [tutorialDismissed, setTutorialDismissed] = useState(() => {
    return localStorage.getItem('firstRunTutorialSeen') === 'true';
  });
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState<boolean | null>(null);
  const playCelebrationSound = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Success sound: ascending notes
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      const startTime = audioContext.currentTime + index * 0.15;
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.4);
    });
  };
  const handleFinishRun = async (
    gpsPoints: TrackingPoint[], 
    trackMetrics: TrackingServiceMetrics,
    runDistance: number,
    runDuration: number,
    runPace: number,
    runCalories: number
  ) => {
    const span = logger.startSpan('handleFinishRun', {
      duration: runDuration,
      distance: runDistance,
      pointsCount: gpsPoints.length
    });
    try {
      hapticSuccess();
      
      // Update global store
      runSession.finishRun();
      runSession.setPartialState({
        distance: runDistance,
        duration: runDuration,
        pace: runPace,
        calories: runCalories,
        splits: state.splits,
        polyline: state.polyline,
      });

      // Play celebration sound
      playCelebrationSound();

      // Parar BPM monitoring com timeout
      let bpmReadings: any[] = [];
      try {
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('BPM timeout')), 3000));
        bpmReadings = (await Promise.race([bpm.stopMonitoring(), timeout])) as any[];
        logger.info('BPM readings collected', {
          count: bpmReadings.length
        });
      } catch (bpmError) {
        logger.warn('BPM collection failed (optional)', {
          error: bpmError
        });
        // Continuar sem BPM
      }

      // Celebrate with sound only (confetti moved to summary page)
      playCelebrationSound();

      // Processar corrida via Edge Function
      setIsProcessing(true);
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão não encontrada');
      }
      toast({
        title: "Processando corrida...",
        description: "Aguarde enquanto salvamos seus dados"
      });

      // Validar dados GPS antes de processar
      if (!gpsPoints || gpsPoints.length === 0) {
        logger.error('No GPS points received in handleFinishRun', {
          received: gpsPoints,
          trackingPointsLength: tracking.points.length
        });
        toast({
          title: "❌ Erro Crítico",
          description: "Nenhum ponto GPS foi coletado. Tente novamente.",
          variant: "destructive"
        });
        setIsProcessing(false);
        span.end('error');
        return;
      }
      
      if (gpsPoints.length < 5) {
        logger.warn('Insufficient GPS points collected', {
          pointsCount: gpsPoints.length,
          duration: runDuration
        });
        toast({
          title: "⚠️ Poucos Dados GPS",
          description: `Coletados apenas ${gpsPoints.length} pontos GPS. Para melhores resultados, corra por mais tempo (mínimo 30 segundos) e aguarde o GPS ativar.`,
          variant: "default",
          duration: 6000
        });
      }

      // Checar se está offline
      if (!networkStatus.isOnline) {
        logger.info('Offline detected, saving run locally');

        // Salvar corrida localmente
        const offlineRun: OfflineRun = {
          id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userId: session.user.id,
          startedAt: startTimeRef.current!,
          finishedAt: Date.now(),
          duration: runDuration,
          distance: runDistance,
          pace: runPace,
          calories: runCalories,
          splits: state.splits,
          gpsPoints: gpsPoints.map(p => ({
            lat: p.lat,
            lng: p.lng,
            // Ensure timestamp is always a number (epoch ms)
            timestamp: typeof p.timestamp === 'string' 
              ? new Date(p.timestamp).getTime() 
              : p.timestamp,
            speed: p.speed,
            accuracy: p.accuracy
          })),
          bpmReadings: bpmReadings.map((r: any) => ({
            timestamp: r.timestamp,
            bpm: r.bpm
          })),
          postureData: postureData,
          musicStats: null,
          syncStatus: 'pending',
          syncAttempts: 0
        };
        await offlineDB.saveOfflineRun(offlineRun);
        
        // Save offline summary payload with polyline for local map rendering
        sessionStorage.setItem('offline_run_summary', JSON.stringify({
          distance: runDistance,
          duration: runDuration,
          pace: runPace,
          calories: runCalories,
          splits: state.splits,
          polyline: state.polyline,
          // Include gpsPoints for map rendering fallback
          gpsPoints: gpsPoints.map(p => ({
            lat: p.lat,
            lng: p.lng,
            timestamp: typeof p.timestamp === 'string' 
              ? new Date(p.timestamp).getTime() 
              : p.timestamp,
          })),
        }));
        
        toast({
          title: "✅ Corrida Salva (Offline)",
          description: "Será sincronizada quando a internet voltar",
          duration: 5000
        });
        
        // Navigate to offline summary
        navigate('/run/summary/offline');
        setIsProcessing(false);
        span.end('success');
        return;
      }

      // Processar com timeout de 30s
      const processTimeout = setTimeout(() => {
        logger.error('Process-run timeout after 30s');
        toast({
          title: "⏱️ Timeout",
          description: "Salvamento está demorando muito. Tente novamente.",
          variant: "destructive"
        });
      }, 30000);
      let data: any;
      let savedRun: any;
      try {
        const result = await supabase.functions.invoke('process-run', {
          body: {
            user_id: session.user.id,
            raw_gps_points: gpsPoints.map(p => ({
              lat: p.lat,
              lng: p.lng,
              // Ensure timestamp is always a number (epoch ms)
              timestamp: typeof p.timestamp === 'string' 
                ? new Date(p.timestamp).getTime() 
                : p.timestamp,
              speed: p.speed,
              accuracy: p.accuracy,
              altitude: p.altitude
            })),
            // Enviar métricas calculadas localmente para garantir precisão
            totalDistance: runDistance,        // metros
            elapsedTime: runDuration,          // segundos
            started_at: new Date(Date.now() - runDuration * 1000).toISOString(),
            ended_at: new Date().toISOString(),
            bpm_readings: bpmReadings,
            avg_heart_rate: bpm.avgBPM,
            max_heart_rate: bpm.maxBPM
          }
        });
        clearTimeout(processTimeout);
        if (result.error) throw result.error;
        data = result.data;

        // Validar resposta
        if (!data?.run_id) {
          throw new Error('No run_id returned from process-run');
        }
        setProcessedRunId(data.run_id);

        // Verificar se os dados foram realmente salvos no banco
        const {
          data: verifiedRun,
          error: verifyError
        } = await supabase.from('runs').select('id, distance_km, duration_seconds, pace_avg, calories').eq('id', data.run_id).maybeSingle();
        if (verifyError) {
          throw new Error(`Erro ao verificar dados salvos: ${verifyError.message}`);
        }
        if (!verifiedRun) {
          throw new Error('Corrida não foi salva corretamente no banco de dados');
        }
        savedRun = verifiedRun;
        logger.info('Corrida verificada e salva com sucesso', {
          runId: savedRun.id,
          distance: savedRun.distance_km,
          duration: savedRun.duration_seconds
        });
        toast({
          title: "✅ Corrida Salva",
          description: "Todos os dados foram salvos com sucesso"
        });
      } catch (processError) {
        clearTimeout(processTimeout);
        throw processError;
      }

      // Verificar se há dados GPS suficientes para gerar mapa
      // ✅ Use gpsPoints from process-run payload, not state.polyline (which may be stale)
      const hasGpsData = gpsPoints && Array.isArray(gpsPoints) && gpsPoints.length >= 2;
      if (!hasGpsData) {
        logger.warn('Corrida salva sem dados GPS suficientes para mapa', {
          runId: savedRun.id,
          gpsPointsCount: gpsPoints?.length || 0,
          statePolylineCount: state.polyline?.length || 0
        });
        toast({
          title: "⚠️ Mapa Indisponível",
          description: "Não foi possível capturar dados GPS suficientes para o mapa",
          variant: "destructive",
          duration: 4000
        });
      } else {
        logger.info('Dados GPS disponíveis para geração de mapa', {
          runId: savedRun.id,
          gpsPoints: gpsPoints.length
        });
      }

      // Mark run as finished in sessionStorage to prevent auto-resume
      sessionStorage.setItem('run_finished', 'true');
      sessionStorage.removeItem('active_run_state');

      // Save music history with actual run ID
      musicHistory.startSession(data.run_id);
      await musicHistory.saveHistory();

      // Check for new achievements (now comes from process-run automatically)
      if (data.new_achievements && data.new_achievements.length > 0) {
        setUnlockedBadges(data.new_achievements.map((a: any) => ({
          id: a.achievement_type || a.id,
          title: a.title,
          description: a.description,
          icon: a.icon || '🏆'
        })));
        setShowBadgeAnimation(true);

        // Toast para cada achievement
        data.new_achievements.forEach((achievement: any) => {
          toast({
            title: "🏆 Nova Conquista!",
            description: `${achievement.title} - ${achievement.description}`,
            duration: 5000
          });
        });

        // Confetti celebratório!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: {
            y: 0.6
          }
        });
      }

      // Close any open modals/sheets
      setShowBadgeAnimation(false);
      
      // Navigate to summary page (success confetti will play there)
      runSession.setProcessedRunId(data.run_id);
      navigate(`/run/summary/${data.run_id}`);

      span.end('success');
    } catch (error) {
      logger.error('Erro ao processar corrida', error, {
        distance: state.distance,
        duration: state.duration
      });
      toast({
        title: "Erro",
        description: "Não foi possível salvar a corrida",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Pré-carregar geolocalização e avatar ao montar componente
  useEffect(() => {
    // Buscar avatar do usuário
    const fetchUserAvatar = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const {
        data
      } = await supabase.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (data?.avatar_url) {
        setUserAvatar(data.avatar_url);
      }
    };

    // Checar se é primeira corrida
    const checkFirstRun = async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;
      const {
        data
      } = await supabase.from('runs').select('id').eq('user_id', user.id).limit(1);
      setIsFirstRun(!data || data.length === 0);
    };
    fetchUserAvatar();
    checkFirstRun();

    // Verificar geolocalização
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada');
      setLocationLoading(false);
      return;
    }

    // Verificar localStorage primeiro (persistência de permissão)
    const savedPermission = localStorage.getItem('location_permission_granted');
    if (savedPermission === 'true') {
      setLocationPermissionGranted(true);
      requestLocation();
      return;
    }

    // Verificar se permissão já foi concedida antes
    if ('permissions' in navigator) {
      navigator.permissions.query({
        name: 'geolocation'
      }).then(result => {
        if (result.state === 'granted') {
          setLocationPermissionGranted(true);
          localStorage.setItem('location_permission_granted', 'true');
          requestLocation();
        } else if (result.state === 'denied') {
          setLocationPermissionGranted(false);
          localStorage.removeItem('location_permission_granted');
          setLocationError('Permissão de localização negada');
          setLocationLoading(false);
        } else {
          // Prompt - mostrar dialog customizado
          setShowLocationDialog(true);
          setLocationLoading(false);
        }
      }).catch(() => {
        // Fallback se permissions API não estiver disponível
        requestLocation();
      });
    } else {
      requestLocation();
    }
  }, [toast]);
  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(position => {
      setInitialLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
      setLocationLoading(false);
      setLocationPermissionGranted(true);
      localStorage.setItem('location_permission_granted', 'true');
      logger.info('Localização inicial carregada', {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
    }, error => {
      logger.error('Erro ao obter localização inicial', error);
      setLocationError('Permissão de localização negada');
      setLocationPermissionGranted(false);
      localStorage.removeItem('location_permission_granted');
      setLocationLoading(false);
      toast({
        variant: 'destructive',
        title: 'Localização Desabilitada',
        description: 'Por favor, ative a localização para usar o app'
      });
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  const handleLocationPermissionConfirm = () => {
    setShowLocationDialog(false);
    setLocationLoading(true);
    requestLocation();
  };

  // RunMatch: Escutar por encounters quando correndo
  useEffect(() => {
    if (state.status !== 'running' || invisibleMode) return;
    const fetchUserId = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return null;
      return session.user.id;
    };
    const setupRealtimeSubscription = async () => {
      const userId = await fetchUserId();
      if (!userId) return;
      const channel = supabase.channel('runner-encounters-realtime').on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'runner_encounters',
        filter: `encountered_user_id=eq.${userId}`
      }, async payload => {
        // Buscar detalhes completos do encounter
        const {
          data
        } = await supabase.from('runner_encounters').select('*').eq('id', payload.new.id).single();
        if (data) {
          setCurrentEncounter(data);
          toast({
            title: "🏃 Runner detectado próximo!",
            description: `A ${data.distance_meters}m de você`
          });
        }
      }).subscribe();
      return () => {
        supabase.removeChannel(channel);
      };
    };
    setupRealtimeSubscription();
  }, [state.status, invisibleMode, toast]);
  const handleAcceptMatch = () => {
    setDuoRunMode(true);
    setCurrentEncounter(null);
    toast({
      title: "🤝 Duo Run iniciado!",
      description: "Vocês estão correndo juntos agora"
    });
  };
  const handleDeclineMatch = () => {
    setCurrentEncounter(null);
  };
  const handleGoInvisible = () => {
    setInvisibleMode(true);
    setCurrentEncounter(null);
  };
  const toggleInvisibleMode = () => {
    setInvisibleMode(!invisibleMode);
    toast({
      title: invisibleMode ? "👁️ Modo Visível" : "👁️ Modo Invisível",
      description: invisibleMode ? "Você pode receber RunMatch agora" : "Você não receberá notificações de RunMatch"
    });
  };
  
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const formatDistance = (meters: number) => {
    if (meters < 1000) {
      return `${meters.toFixed(0)}m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
  };
  // Track last valid pace to prevent null displays
  const lastValidPaceRef = useRef<number>(0);
  
  // Update last valid pace when current pace is valid
  useEffect(() => {
    if (state.pace && isFinite(state.pace) && state.pace >= 1 && state.pace <= 40) {
      lastValidPaceRef.current = state.pace;
    }
  }, [state.pace]);
  
  const formatPace = (pace: number) => {
    // Use lastValidPace as fallback if current pace is invalid
    const displayPace = (pace && isFinite(pace) && pace >= 1 && pace <= 40) 
      ? pace 
      : lastValidPaceRef.current;
    
    if (!displayPace || !isFinite(displayPace) || displayPace <= 0) {
      return '--:--/min';
    }
    
    const mins = Math.floor(displayPace);
    const secs = Math.floor((displayPace - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}/min`;
  };
  return <>
      {/* Loading Overlay durante processamento */}
      {isProcessing && <LoadingOverlay message="Processando sua corrida..." />}
      
      {/* GPS Status Indicator */}
      {state.status === 'running' && <div className="fixed top-20 left-4 z-50">
          {!gpsStats.isWarmingUp ? <Badge variant="default" className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5">
              <CheckCircle className="w-3.5 h-3.5" />
              GPS Ativo
            </Badge> : <Badge variant="secondary" className="flex items-center gap-1.5 animate-pulse px-3 py-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Inicializando GPS...
            </Badge>}
        </div>}
      
      {/* RunMatch Notification */}
      {currentEncounter && !duoRunMode && <RunMatchNotification encounterId={currentEncounter.id} heartIndex={currentEncounter.heart_index || 75} distance={currentEncounter.distance_meters || 30} onAccept={handleAcceptMatch} onDecline={handleDeclineMatch} onGoInvisible={handleGoInvisible} />}

      {/* Duo Run Summary */}
      {showDuoSummary && duoRunData && <DuoRunSummary totalDistance={duoRunData.total_distance_meters} avgSync={duoRunData.avg_sync_percentage} syncPeaks={duoRunData.sync_peaks} badgesEarned={duoRunData.badges_earned || []} onClose={() => {
      setShowDuoSummary(false);
      setDuoRunData(null);
      setDuoRunMode(false);
    }} />}

        
        {/* Duo Run Mode Overlay */}
        {duoRunMode && partnerLocation && <DuoRunMode encounterId={currentEncounter?.id || ''} myLocation={
          state.polyline.length > 0 
            ? state.polyline[state.polyline.length - 1] 
            : tracking.points.length > 0
              ? { lat: tracking.points[tracking.points.length - 1].lat, lng: tracking.points[tracking.points.length - 1].lng }
              : trackingService.getLastKnownPosition() || initialLocation || { lat: -23.5505, lng: -46.6333 }
        } partnerLocation={partnerLocation} />}

        {/* Map with dark style - aligned with header */}
        <div className="relative w-full h-[50vh] overflow-hidden transition-all duration-500">
          {/* Visibility Toggle - Overlay on top right of map */}
          {state.status === 'running' && (
            <div className="absolute top-3 right-3 z-20">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={toggleInvisibleMode}
                className="bg-background/80 backdrop-blur-sm shadow-lg"
              >
                {invisibleMode ? <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Invisível
                </> : <>
                  <Eye className="w-4 h-4 mr-2" />
                  Visível
                </>}
              </Button>
            </div>
          )}
          
          {/* Use tracking.points directly for instant position updates */}
          {(state.polyline.length > 0 || tracking.points.length > 0) ? state.status === 'running' ? <EnhancedRunMap points={tracking.points.map(p => ({
        lat: p.lat,
        lng: p.lng,
        timestamp: p.timestamp,
        pace: state.pace,
        speed: state.currentSpeed
      }))} currentPosition={getSafeMapCenter(state.polyline, tracking.points.map(p => ({ lat: p.lat, lng: p.lng })), initialLocation)} paceTarget={5.5} // Could be from user settings
      showHeatmap={true} showPOI={true} height="50vh" avatarUrl={userAvatar || undefined} currentAccuracy={tracking.points.length > 0 ? tracking.points[tracking.points.length - 1].accuracy : undefined} /> : <RunMap polyline={state.polyline} center={getSafeMapCenter(state.polyline, tracking.points.map(p => ({ lat: p.lat, lng: p.lng })), initialLocation)} zoom={16} avatarUrl={userAvatar || undefined} accuracy={tracking.points.length > 0 ? tracking.points[tracking.points.length - 1].accuracy : undefined} showPaceHeatmap={true} averagePace={state.pace || 6.0} /> : locationLoading ? <div className="w-full h-full bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center animate-fade-in">
              <div className="text-center text-muted-foreground">
                <MapPin className="w-16 h-16 mx-auto mb-2 opacity-50 animate-pulse" />
                <p className="text-sm">Carregando localização...</p>
              </div>
            </div> : locationError ? <div className="w-full h-full bg-gradient-to-br from-destructive/10 to-destructive/5 flex items-center justify-center animate-fade-in">
              <div className="text-center text-destructive animate-scale-in">
                <MapPin className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">{locationError}</p>
                <p className="text-xs mt-1">Ative a localização nas configurações</p>
              </div>
            </div> : initialLocation ? <RunMap polyline={[initialLocation]} center={initialLocation} zoom={16} avatarUrl={userAvatar || undefined} accuracy={tracking.points.length > 0 ? tracking.points[tracking.points.length - 1].accuracy : undefined} showPaceHeatmap={false} /> : <div className="w-full h-full bg-gradient-to-br from-muted/50 to-muted flex items-center justify-center animate-fade-in">
              <div className="text-center text-muted-foreground animate-pulse">
                <MapPin className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aguardando localização...</p>
              </div>
            </div>}
        </div>

        {/* Elevation Profile - show during running AND paused */}
        {(state.status === 'running' || state.status === 'paused') && tracking.points.length > 2 && <div className="px-4 pt-4 -mt-4">
            <ElevationProfile points={tracking.points} currentDistance={state.distance} />
          </div>}

        {/* Metrics */}
        <div className="p-4 pb-20 space-y-4">
          {/* Active Segment Overlay */}
          {activeSegment && currentLat && currentLng && <ActiveSegmentOverlay segment={activeSegment} currentLat={currentLat} currentLng={currentLng} />}

          <Card className="relative z-10 -mt-24 shadow-2xl py-0 mx-0 my-px">
            <CardContent className="p-6 py-[10px] mx-0 my-[20px]">
              <div className="grid grid-cols-2 gap-4">
                {/* Tempo */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                    <Clock className="w-4 h-4" />
                    <span>Tempo</span>
                  </div>
                  <p className="text-3xl font-bold">{formatTime(state.duration)}</p>
                </div>

                {/* Distância */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                    <Route className="w-4 h-4" />
                    <span>Distância</span>
                  </div>
                  <p className="text-3xl font-bold">{formatDistance(state.distance)}</p>
                </div>

                {/* Pace */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                    <Zap className="w-4 h-4" />
                    <span>Pace</span>
                  </div>
                  <p className="text-2xl font-semibold">{formatPace(state.pace)}</p>
                </div>

                {/* Calorias */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                    <Flame className="w-4 h-4" />
                    <span>Calorias</span>
                  </div>
                  <p className="text-2xl font-semibold">{state.calories}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Heart Rate Card - Durante a corrida */}
          {state.status === 'running' && (
            <HeartRateCard
              currentBPM={bpm.currentBPM}
              avgBPM={bpm.avgBPM}
              maxBPM={bpm.maxBPM}
              zone={bpm.zone}
              isMonitoring={bpm.isMonitoring}
              deviceConnected={bpm.isMonitoring && bpm.currentBPM > 0}
              source={bpm.isMonitoring ? 'ble' : 'none'}
              onConnect={() => bpm.requestConnection()}
            />
          )}

          {/* Splits */}
          {state.splits.length > 0 && <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5" />
                  <h3 className="font-semibold">Splits (km)</h3>
                </div>
                <div className="space-y-2">
                  {state.splits.map((split, idx) => <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Km {idx + 1}</span>
                      <span className="font-mono">{formatPace(split.pace)}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>}

          {/* BPM Monitor Card - Antes de iniciar corrida */}
          {state.status === 'idle' && <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Heart className={`w-6 h-6 ${bpm.isMonitoring ? 'text-red-500 animate-pulse' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="font-semibold">Monitor Cardíaco</p>
                      <p className="text-xs text-muted-foreground">
                        {bpm.isMonitoring ? `${bpm.currentBPM} BPM` : 'Não conectado'}
                      </p>
                    </div>
                  </div>
                  
                  <Button variant={bpm.isMonitoring ? "outline" : "default"} size="sm" onClick={async () => {
              if (bpm.isMonitoring) {
                await bpm.stopMonitoring();
                toast({
                  title: "Sensor BPM Desconectado",
                  description: "Monitor cardíaco desativado"
                });
              } else {
                bpm.requestConnection();
              }
            }}>
                    {bpm.isMonitoring ? 'Desconectar' : 'Conectar'}
                  </Button>
                </div>
                
                {/* Indicadores discretos durante scanning/connecting */}
                {bpm.status === 'scanning' && (
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground animate-pulse mt-3 pt-3 border-t">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Procurando monitor BLE...
                  </div>
                )}
                
                {bpm.status === 'connecting' && (
                  <div className="flex items-center justify-center gap-2 text-xs text-blue-500 mt-3 pt-3 border-t">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Conectando...
                  </div>
                )}
              </CardContent>
            </Card>}

          {/* GPS Diagnostic Badge - Durante corrida */}
          {state.status === 'running' && <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <GPSDiagnosticBadge pointsCollected={gpsStats.pointsCollected} pointsRejected={gpsStats.pointsRejected} currentAccuracy={gpsStats.currentAccuracy} isWarmingUp={gpsStats.isWarmingUp} />
                  
                  {/* Botão de diagnóstico detalhado */}
                  {(import.meta.env.DEV || tracking.points.length < 5) && <Button size="sm" variant="outline" onClick={() => {
              const rejected = (tracking as any).getRejectedPointsStats?.() || {
                total: 0,
                accuracy: 0,
                speed: 0,
                distance: 0
              };
              const stats = {
                pointsCollected: tracking.points.length,
                pointsRejected: rejected.total || 0,
                rejectedByAccuracy: rejected.accuracy || 0,
                rejectedBySpeed: rejected.speed || 0,
                rejectedByDistance: rejected.distance || 0,
                currentAccuracy: tracking.points[tracking.points.length - 1]?.accuracy,
                isNative: Capacitor.isNativePlatform(),
                platform: Capacitor.getPlatform()
              };
              logger.info('🔍 GPS Diagnostic', stats);
              toast({
                title: "🔍 Diagnóstico GPS",
                description: `✅ ${stats.pointsCollected} pontos coletados
❌ ${stats.pointsRejected} pontos rejeitados
📊 Accuracy: ${stats.currentAccuracy ? Math.round(stats.currentAccuracy) + 'm' : 'N/A'}
📱 Plataforma: ${stats.platform}`,
                duration: 10000
              });
            }} className="gap-2">
                      <Signal className="w-4 h-4" />
                      Diagnóstico GPS
                    </Button>}
                </div>
              </CardContent>
            </Card>}

          {/* BPM Display durante corrida */}
          {(state.status === 'running' || state.status === 'paused') && bpm.isMonitoring && <BPMDisplay currentBPM={bpm.currentBPM} avgBPM={bpm.avgBPM} maxBPM={bpm.maxBPM} zone={bpm.zone} isMonitoring={bpm.isMonitoring} />}

          {/* Compact Music Player durante corrida */}
          {(state.status === 'running' || state.status === 'paused') && adaptiveMusic.currentTrack && <CompactMusicPlayer currentTrack={adaptiveMusic.currentTrack} isPlaying={adaptiveMusic.isPlaying} currentBPM={adaptiveMusic.currentBPM} isSynced={adaptiveMusic.isSynced} isAdjusting={adaptiveMusic.isAdjusting} onPlayPause={async () => {
        if (adaptiveMusic.isPlaying) {
          await adaptiveMusic.pause();
        } else {
          await adaptiveMusic.resume();
        }
      }} onNext={async () => {
        // Track current song before moving to next
        if (adaptiveMusic.currentTrack) {
          musicHistory.trackPlayed(adaptiveMusic.currentTrack, adaptiveMusic.currentBPM, adaptiveMusic.isSynced, adaptiveMusic.isAdjusting);
        }
        await adaptiveMusic.next();
      }} />}

          {/* Real-Time AI Coach */}
          {state.status === 'running' && <RealTimeAICoach currentBPM={bpm.currentBPM} currentPace={state.pace} targetPace={5.5} zone={bpm.zone?.name} distance={state.distance} duration={state.duration} isRunning={true} />}

          {/* Adaptive Music Player */}
          {(state.status === 'running' || state.status === 'paused') && <AdaptiveMusicPlayer currentBPM={bpm.currentBPM} zone={bpm.zone?.name} isRunning={state.status === 'running'} />}

          {/* Tutorial primeira corrida */}
          {isFirstRun && !tutorialDismissed && state.status === 'idle' && <Card className="bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30 relative">
              <CardContent className="p-4">
                <Button variant="ghost" size="icon" className="absolute top-2 right-2 w-8 h-8 rounded-full hover:bg-primary/10" onClick={() => {
            localStorage.setItem('firstRunTutorialSeen', 'true');
            setTutorialDismissed(true);
          }} aria-label="Fechar dica">
                  <X className="w-4 h-4" />
                </Button>
                <h3 className="font-bold mb-2 flex items-center gap-2 text-primary">
                  💡 Primeira Corrida?
                </h3>
                <ul className="text-sm space-y-1.5 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Aguarde o GPS ativar (3-5 segundos)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Corra por pelo menos 30 segundos para dados precisos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>Mantenha o celular no bolso ou braçadeira</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>O app funciona em segundo plano</span>
                  </li>
                </ul>
              </CardContent>
            </Card>}

          {/* Controls */}
          <div className="space-y-2">
            {state.status === 'idle' && (
              <Button 
                size="lg" 
                className="w-full h-16 text-lg bg-green-600 hover:bg-green-700" 
                onClick={async () => {
                  try {
                    // Show map immediately for visual feedback
                    setShowMap(true);
                    
                    // Start run (includes GPS tracking)
                    await startRun();
                    
                    // Start adaptive music if connected
                    if (adaptiveMusic.isAuthenticated && bpm.currentBPM > 0) {
                      await adaptiveMusic.startMusic();
                    }
                    
                    analytics.trackRunStarted({ userId: 'current_user' });
                  } catch (error) {
                    logger.error('Failed to start run', { error });
                    setShowMap(false);
                    toast({
                      variant: 'destructive',
                      title: 'Erro ao iniciar',
                      description: 'Verifique as permissões de localização.',
                    });
                  }
                }}
              >
                <Play className="w-6 h-6 mr-2" />
                Iniciar Corrida
              </Button>
            )}

            {/* Route Suggestions - AFTER GPS button */}
            {state.status === 'idle' && (
              <Sheet open={showRouteSuggestions} onOpenChange={setShowRouteSuggestions}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full" onClick={() => {
                    if (!initialLocation) {
                      toast({
                        variant: 'destructive',
                        title: 'Localização necessária',
                        description: 'Ative a localização para ver sugestões de rotas',
                      });
                      return;
                    }
                    if (routeSuggestions.suggestions.length === 0) {
                      routeSuggestions.fetchSuggestions(initialLocation.lat, initialLocation.lng);
                    }
                  }}>
                    <Map className="w-4 h-4 mr-2" />
                    Sugestões de Rotas IA
                    {!initialLocation && <AlertCircle className="w-3 h-3 ml-1 text-yellow-500" />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[80vh] overflow-y-auto ybr-scroll-stable">
                  <div className="py-4">
                    <h2 className="text-2xl font-bold mb-4">Rotas Recomendadas</h2>
                    {!initialLocation ? (
                      <div className="text-center p-8 text-muted-foreground">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                        <p className="font-medium mb-2">Localização desativada</p>
                        <p className="text-sm">
                          Ative a localização para ver rotas recomendadas na sua região.
                        </p>
                      </div>
                    ) : routeSuggestions.loading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : routeSuggestions.suggestions.length === 0 ? (
                      <div className="text-center p-8 text-muted-foreground">
                        <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium mb-2">Nenhuma rota disponível</p>
                        <p className="text-sm">
                          Ainda não há rotas cadastradas na sua região. Volte em breve!
                        </p>
                      </div>
                    ) : (
                      <RouteSuggestions 
                        suggestions={routeSuggestions.suggestions} 
                        onSave={routeSuggestions.saveRoute} 
                        onUnsave={routeSuggestions.unsaveRoute} 
                      />
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            )}

            {/* Tourist Routes - AFTER Route Suggestions */}
            {state.status === 'idle' && (
              <>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => {
                    if (!initialLocation) {
                      toast({
                        variant: 'destructive',
                        title: 'Localização necessária',
                        description: 'Ative a localização para ver rotas turísticas',
                      });
                      return;
                    }
                    setShowTouristRoutes(true);
                  }}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Rotas Turísticas 🏛️
                  {!initialLocation && <AlertCircle className="w-3 h-3 ml-1 text-yellow-500" />}
                </Button>
                
                <TouristRoutesSheet
                  isOpen={showTouristRoutes}
                  onClose={() => setShowTouristRoutes(false)}
                  userLocation={initialLocation}
                  onRouteSelect={(route) => {
                    logger.info('[Run] Tourist route selected', { routeId: route.id, name: route.name });
                    if (route.route_points && route.route_points.length > 0) {
                      setState(prev => ({
                        ...prev,
                        polyline: route.route_points.map(p => ({ 
                          lat: p.lat, 
                          lng: p.lng, 
                          timestamp: Date.now() 
                        }))
                      }));
                      toast({
                        title: `Rota carregada: ${route.name}`,
                        description: `Distância: ${route.distance_km} km · Tempo estimado: ${route.estimated_time_min} min`,
                        duration: 5000,
                      });
                    }
                  }}
                />
              </>
            )}

            {state.status === 'running' && <>
                <Button size="lg" variant="secondary" className="w-full h-14" onClick={() => {
            pauseRun();
            tracking.pauseTracking();
            adaptiveMusic.pause();
            analytics.trackRunPaused({
              duration: state.duration,
              distance: state.distance
            });
          }}>
                  <Pause className="w-5 h-5 mr-2" />
                  Pausar
                </Button>
                
                <Button size="sm" variant="outline" className="w-full" onClick={() => setShowAdvancedMetrics(!showAdvancedMetrics)}>
                  {showAdvancedMetrics ? 'Ocultar Métricas' : 'Ver Métricas Avançadas'}
                </Button>
              </>}

            {state.status === 'paused' && <>
                <Button size="lg" className="w-full h-14" onClick={() => {
            resumeRun();
            tracking.resumeTracking();
            adaptiveMusic.resume();
            analytics.trackRunResumed({
              duration: state.duration,
              distance: state.distance
            });
          }}>
                  <Play className="w-6 h-6 mr-2" />
                  Retomar
                </Button>
                <Button size="lg" variant="destructive" className="w-full h-14" onClick={async () => {
            // Capturar valores ANTES de parar o tracking
            let capturedDistance = state.distance;
            let capturedDuration = state.duration;
            let capturedPace = state.pace;
            let capturedCalories = state.calories;
            
            // ✅ DEBUG LOG: State before stopTracking
            logger.info('🔴 STOPPING RUN - State before stopTracking', {
              capturedDistance,
              capturedDuration,
              capturedPace,
              capturedCalories,
              stateDistance: state.distance,
              stateDuration: state.duration
            });
            
            const {
              points,
              metrics: trackMetrics
            } = await tracking.stopTracking();
            
            // ✅ DEBUG LOG: Points received from stopTracking
            logger.info('🔴 STOPPING RUN - Points received from stopTracking', {
              pointsReceived: points.length,
              firstPoint: points[0] ? { lat: points[0].lat.toFixed(6), lng: points[0].lng.toFixed(6) } : null,
              lastPoint: points[points.length - 1] ? { 
                lat: points[points.length - 1].lat.toFixed(6), 
                lng: points[points.length - 1].lng.toFixed(6) 
              } : null,
              capturedDistance,
              capturedDuration
            });
            
            // ✅ DEFENSIVE VALIDATION: Check for empty points
            if (points.length === 0) {
              logger.error('🚨 CRITICAL: Received 0 GPS points from stopTracking!', {
                capturedDistance,
                capturedDuration,
                checkBackup: sessionStorage.getItem('gps_points_backup')
              });
              
              toast({
                variant: 'destructive',
                title: 'Erro ao salvar corrida',
                description: 'Nenhum ponto GPS coletado. Verifique permissões de localização.',
                duration: 5000,
              });
              
              return;
            }
            
            // ✅ FALLBACK: If state has zero metrics but we have GPS points, recalculate from points
            if ((capturedDistance === 0 || capturedDuration === 0) && points.length >= 2) {
              logger.warn('🔧 State metrics are zero, recalculating from GPS points', {
                stateDistance: capturedDistance,
                stateDuration: capturedDuration,
                pointsCount: points.length
              });
              
              // Recalculate distance from GPS points
              let recalcDistance = 0;
              for (let i = 1; i < points.length; i++) {
                const prev = points[i - 1];
                const curr = points[i];
                recalcDistance += haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng);
              }
              
              // Recalculate duration from GPS timestamps
              const firstTs = points[0].timestamp;
              const lastTs = points[points.length - 1].timestamp;
              const recalcDuration = Math.floor((lastTs - firstTs) / 1000);
              
              // Use recalculated values if state was zero
              if (capturedDistance === 0 && recalcDistance > 0) {
                capturedDistance = recalcDistance;
                logger.info('✅ Distance recalculated from GPS points', { recalcDistance });
              }
              if (capturedDuration === 0 && recalcDuration > 0) {
                capturedDuration = recalcDuration;
                logger.info('✅ Duration recalculated from GPS timestamps', { recalcDuration });
              }
              
              // Recalculate pace and calories
              const distanceKm = capturedDistance / 1000;
              if (distanceKm > 0 && capturedDuration > 0) {
                capturedPace = (capturedDuration / 60) / distanceKm;
                capturedCalories = Math.round(distanceKm * 60); // ~60 kcal/km estimate
              }
              
              logger.info('✅ Metrics recalculated from GPS points', {
                distance: capturedDistance,
                duration: capturedDuration,
                pace: capturedPace,
                calories: capturedCalories
              });
            }
            
            await handleFinishRun(
              points, 
              trackMetrics,
              capturedDistance,
              capturedDuration,
              capturedPace,
              capturedCalories
            );
            
            analytics.trackRunEnded({
              duration: capturedDuration,
              distance: capturedDistance,
              pace: capturedPace,
              calories: capturedCalories,
              totalPoints: points.length,
              metrics: {
                ...trackMetrics,
                avgBPM: bpm.avgBPM,
                maxBPM: bpm.maxBPM
              }
            });
          }}>
                  <Square className="w-5 h-5 mr-2" />
                  Finalizar
                </Button>
              </>}
            
            {/* Advanced Metrics */}
            {showAdvancedMetrics && state.status === 'running' && <Card className="mt-4">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">📊 Métricas Avançadas</h3>
                    <Button size="sm" variant="ghost" onClick={() => setShowAdvancedMetrics(false)}>
                      Fechar
                    </Button>
                  </div>
                  
                  {/* Real-time AI Feedback */}
                  <RealTimeFeedback currentPace={state.pace} targetPace={6.0} currentSpeed={state.currentSpeed || 0} duration={state.duration} distance={state.distance / 1000} />
                  
                  <TrackingMetrics metrics={tracking.metrics} />
                  
                  {/* Posture Analysis */}
                  <PostureAnalysis isRunning={state.status === 'running'} onDataUpdate={data => setPostureData(data)} />
                  
                  {bpm.currentBPM > 0 && bpm.zone && <BPMDisplay currentBPM={bpm.currentBPM} avgBPM={bpm.avgBPM} maxBPM={bpm.maxBPM} zone={bpm.zone} isMonitoring={bpm.isMonitoring} />}
                  
                  {adaptiveMusic.currentTrack && <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <Music className="w-5 h-5 text-primary" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{adaptiveMusic.currentTrack.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{adaptiveMusic.currentTrack.artist}</p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {adaptiveMusic.currentTrack.bpm} BPM
                      </div>
                    </div>}
                </CardContent>
              </Card>}
          </div>
        </div>

      {/* Location Permission Dialog */}
      <LocationPermissionDialog open={showLocationDialog} onOpenChange={setShowLocationDialog} onConfirm={handleLocationPermissionConfirm} />

      {/* BLE Connection Dialog */}
      <BLEConnectionDialog open={bpm.showConnectionDialog} onOpenChange={bpm.setShowConnectionDialog} onConfirm={async () => {
      bpm.setShowConnectionDialog(false);
      const tempSessionId = `temp-${Date.now()}`;
      await bpm.startMonitoring(tempSessionId);
    }} />

      {/* BPM Status Dialog - Para TODOS os status de erro */}
      <BPMStatusDialog 
        open={
          bpm.status === 'error' || 
          bpm.status === 'error_bluetooth_disabled' || 
          bpm.status === 'error_no_device' ||
          bpm.status === 'error_samsung'
        } 
        status={bpm.status} 
        onCancel={() => {
          // Show toast FIRST, don't block on stopMonitoring
          toast({
            title: 'Conexão Cancelada',
            description: 'Continuando sem monitor cardíaco'
          });
          
          // Try to stop with timeout - don't block UI
          Promise.race([
            bpm.stopMonitoring(),
            new Promise(resolve => setTimeout(resolve, 2000))
          ]).catch(err => {
            logger.warn('Could not stop BPM monitoring cleanly', err);
          });
        }} 
      />
    </>;
};
export default Run;